(function () {
  'use strict';

  const { gsap } = window;
  const { layout, subEvents, PUB_SUB_EVENTS, animProp, easePower4, easePower2 } = window.Universe;

  let _editorSelectedSectionId = null;

  class CartDrawer {
    constructor() {
      this.init();
    }

    setup() {
      this.ctx = gsap.context(() => {}, this.DOM.wrap);

      this.ctx.add(() => {
        for (let i = 0; i < this.DOM.cartOpeners.length; i++) {
          this.DOM.cartOpeners[i].addEventListener('click', this.onOpen);
        }

        for (let i = 0; i < this.DOM.cartClosers.length; i++) {
          this.DOM.cartClosers[i].addEventListener('click', this.onClose);
        }

        this.cartDrawerUpdate = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.onOpen);
        this.cartAccessibilityUpdate = subEvents.subscribe(PUB_SUB_EVENTS.cartUpdate, this.updateAccessibility);

        return () => {
          for (let i = 0; i < this.DOM.cartOpeners.length; i++) {
            this.DOM.cartOpeners[i].removeEventListener('click', this.onOpen);
          }

          for (let i = 0; i < this.DOM.cartClosers.length; i++) {
            this.DOM.cartClosers[i].removeEventListener('click', this.onClose);
          }

          if (this.cartDrawerUpdate) this.cartDrawerUpdate();
          if (this.cartAccessibilityUpdate) this.cartAccessibilityUpdate();
        };
      });

      this.setupTerms();
    }

    setupTerms() {
      if (this.DOM.terms && this.DOM.atc) {
        const cartEmpty = this.DOM.wrap?.querySelector('.js-cart-drawer-items')?.children.length === 0;
        if (cartEmpty) {
          sessionStorage.removeItem('cart_terms_accepted');
        }

        const savedTermsState = sessionStorage.getItem('cart_terms_accepted');
        const browserChecked = this.DOM.terms.checked;

        this.termsAccepted = savedTermsState === 'true' || browserChecked;

        this.DOM.terms.checked = this.termsAccepted;

        this.DOM.terms.addEventListener('change', this.handleChange);

        this.DOM.termsWrapper = this.DOM.wrap.querySelector('.cart-drawer__terms');
        if (this.DOM.termsWrapper) {
          this.onTermsKeyDown = (e) => {
            if ((e.code === 'Enter' || e.code === 'Space') && e.target === this.DOM.termsWrapper) {
              e.preventDefault();
              this.DOM.terms.click();
            }
          };
          this.DOM.termsWrapper.addEventListener('keydown', this.onTermsKeyDown);
        }

        this.handleChange();
      } else if (this.DOM.atc) {
        this.termsAccepted = true;
        this.DOM.atc.forEach((atc) => {
          atc.disabled = false;
          atc.removeAttribute('aria-disabled');
        });
      }
    }

    refresh() {
      if (this.DOM.termsWrapper && this.onTermsKeyDown) {
        this.DOM.termsWrapper.removeEventListener('keydown', this.onTermsKeyDown);
      }
      if (this.DOM.terms) {
        this.DOM.terms.removeEventListener('change', this.handleChange);
      }

      this.DOM.terms = this.DOM.wrap.querySelector('.js-cart-drawer-terms');
      this.DOM.atc = this.DOM.wrap.querySelectorAll('.js-cart-drawer-atc');

      if (this.DOM.terms) {
        this.handleChange(undefined, this.termsAccepted);
      }
      this.setupTerms();
    }

    destroy() {
      if (this.DOM) {
        if (this.ctx) {
          this.ctx.revert();
        }

        document.removeEventListener('keydown', this.onKeyDown);
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('shopify:section:select', this.onEditorSelect);
        document.removeEventListener('shopify:block:select', this.onEditorSelect);
        document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.removeEventListener('shopify:block:deselect', this.onEditorDeselect);

        if (this.onTemplateUpdated) {
          document.removeEventListener('cart-drawer:template-updated', this.onTemplateUpdated);
        }

        if (this.DOM.termsWrapper && this.onTermsKeyDown) {
          this.DOM.termsWrapper.removeEventListener('keydown', this.onTermsKeyDown);
        }
        if (this.DOM.terms) {
          this.DOM.terms.removeEventListener('change', this.handleChange);
        }
      }
    }

    reinit() {
      this.destroy();
      this.init();
    }

    init(container = document) {
      const cartOpeners = container.querySelectorAll('.js-cart-opener');
      const wrap = container.querySelector('.js-cart-drawer-wrap');

      if (cartOpeners.length && wrap) {
        this.DOM = {};
        this.DOM.cartOpeners = cartOpeners;
        this.DOM.cartClosers = container.querySelectorAll('.js-cart-closer');
        this.DOM.wrap = wrap;
        this.DOM.overlay = this.DOM.wrap.querySelector('.js-cart-drawer-overlay');
        this.DOM.drawer = this.DOM.wrap.querySelector('.js-cart-drawer');
        this.DOM.crossSell = this.DOM.wrap.querySelector('.js-cart-drawer-cross-sell');
        this.DOM.terms = this.DOM.wrap.querySelector('.js-cart-drawer-terms');
        this.DOM.atc = this.DOM.wrap.querySelectorAll('.js-cart-drawer-atc');

        this.isOpen = false;
        this.isShipOpen = false;
        this.isAnimating = false;

        this.updateAccessibility = (data) => {
          let count = data?.cartData?.item_count;

          if (count === undefined) {
            const counter = document.querySelector('.js-cart-total-counter');
            count = counter ? parseInt(counter.textContent) : 0;
          }

          const label = window.accessibilityStrings?.openCart || 'Open Cart';
          const suffix = count === 1 ? 'element' : 'elements';
          const ariaLabel = label + ' ' + count + ' ' + suffix;

          for (let i = 0; i < this.DOM.cartOpeners.length; i++) {
            this.DOM.cartOpeners[i].setAttribute('aria-label', ariaLabel);
          }
        };

        this.updateTerms = (override) => {
          this.termsAccepted = override || this.DOM.terms?.checked;

          if (override) this.DOM.terms.checked = this.termsAccepted;
        };

        this.handleChange = (_, override) => {
          this.updateTerms(override);

          sessionStorage.setItem('cart_terms_accepted', this.termsAccepted.toString());

          this.DOM.atc.forEach((atc) => {
            atc.disabled = !this.termsAccepted;
            atc.setAttribute('aria-disabled', this.termsAccepted ? 'false' : 'true');
          });
        };

        this.onOpen = (e) => {
          if (
            e?.source === 'cart-items' ||
            e?.source === 'discount-code' ||
            e?.source === 'product-form' ||
            e?.source === 'gift-wrap'
          )
            return;

          if (e?.currentTarget instanceof Element) {
            this.activeTrigger = e.currentTarget;
          }

          if (this.isOpen) return;

          this.open();
        };

        this.onTemplateUpdated = (e) => {
          if (e.detail?.source === 'product-form' && !this.isOpen) {
            this.open();
          }
        };

        document.addEventListener('cart-drawer:template-updated', this.onTemplateUpdated);

        this.onClose = () => {
          if (this.isEditorSelected) {
            this.isEditorSelected = false;
            _editorSelectedSectionId = null;
          }
          this.close();
        };

        this.onKeyDown = (evt) => {
          const isTabPressed = evt.key === 'Tab' || evt.keyCode === 9;
          const isEscapePressed = evt.key === 'Escape' || evt.code === 'Escape';

          if (isEscapePressed) {
            if (this.isEditorSelected) {
              this.isEditorSelected = false;
              _editorSelectedSectionId = null;
            }
            this.close();
          }

          if (isTabPressed) {
            const focusableEls = this.DOM.drawer.querySelectorAll(
              'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
            );

            if (focusableEls.length === 0) {
              evt.preventDefault();
              return;
            }

            const firstFocusableEl = focusableEls[0];
            const lastFocusableEl = focusableEls[focusableEls.length - 1];

            if (evt.shiftKey) {
              if (document.activeElement === firstFocusableEl) {
                lastFocusableEl.focus();
                evt.preventDefault();
              }
            } else {
              if (document.activeElement === lastFocusableEl) {
                firstFocusableEl.focus();
                evt.preventDefault();
              }
            }
          }
        };

        this.open = () => {
          if (!this.isAnimating) {
            this.isAnimating = true;
            document.body.classList.add('overflow-hidden');
            this.DOM.wrap.classList.add('is-open');

            this.ctx.add(() => {
              const openTl = gsap.timeline({
                onComplete: () => {
                  this.isOpen = true;
                  this.isAnimating = false;

                  document.addEventListener('keydown', this.onKeyDown);
                  const firstFocusable = this.DOM.drawer.querySelector(
                    'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])',
                  );
                  if (firstFocusable) {
                    firstFocusable.focus();
                  }
                },
              });

              openTl
                .set(this.DOM.wrap, {
                  zIndex: 101,
                  visibility: 'visible',
                  pointerEvents: 'all',
                  contentVisibility: 'visible',
                })
                .set(this.DOM.overlay, { display: 'block' }, 0)
                .fromTo(this.DOM.overlay, { autoAlpha: 0 }, { autoAlpha: 1 }, 0)
                .to(
                  this.DOM.drawer,
                  {
                    x: 0,
                    y: 0,
                    xPercent: 0,
                    yPercent: 0,
                    duration: 0.8,
                    ease: easePower4,
                  },
                  0,
                );

            });
          }
        };

        this.close = (immediate = false) => {
          if (!this.isAnimating) {
            this.isAnimating = true;
            this.DOM.wrap.classList.remove('is-open');

            this.ctx.add(() => {
              const closeTl = gsap.timeline({
                onComplete: () => {
                  document.body.classList.remove('overflow-hidden');
                  this.isOpen = false;
                  this.isAnimating = false;

                  document.removeEventListener('keydown', this.onKeyDown);

                  if (this.isShipOpen) {
                    this.shipClose(true);
                  }

                  const selector =
                    '.header-nav__group.header-nav__action-group.header-nav__cart-group.js-header-nav-submenu-item';
                  let targetToFocus = null;

                  if (this.activeTrigger) {
                    targetToFocus = this.activeTrigger.closest(selector);
                  }

                  if (!targetToFocus) {
                    targetToFocus = document.querySelector(selector);
                  }

                  if (targetToFocus) {
                    if (!targetToFocus.hasAttribute('tabindex')) {
                      targetToFocus.setAttribute('tabindex', '-1');
                    }
                    targetToFocus.focus();
                  }

                  this.activeTrigger = null;
                },
              });

              closeTl
                .to(
                  this.DOM.drawer,
                  {
                    xPercent: layout.device >= 2 ? 100 : 0,
                    yPercent: layout.device >= 2 ? 0 : 100,
                    duration: animProp(0.8, immediate),
                    ease: easePower4,
                  },
                  0,
                )
                .to(this.DOM.overlay, { autoAlpha: 0 }, 0)
                .set(this.DOM.overlay, {
                  display: 'none',
                })
                .set(this.DOM.wrap, {
                  zIndex: -1,
                  visibility: 'hidden',
                  pointerEvents: 'none',
                  contentVisibility: 'hidden',
                });
            });
          }
        };

        const getSectionId = () => {
          const sectionEl = this.DOM.wrap.closest('[id^="shopify-section-"]');
          return sectionEl ? sectionEl.id.replace('shopify-section-', '') : null;
        };

        this.isEditorSelected = false;

        this.onEditorSelect = (e) => {
          const sectionId = getSectionId();
          if (!sectionId) return;
          if (e.detail?.sectionId === sectionId) {
            this.isEditorSelected = true;
            _editorSelectedSectionId = sectionId;
            if (!this.isOpen) this.open();
          } else {
            if (this.isEditorSelected) {
              this.isEditorSelected = false;
              _editorSelectedSectionId = null;
              this.close();
            }
          }
        };

        this.onEditorDeselect = (e) => {
          const sectionId = getSectionId();
          if (!sectionId) return;
          if (e.detail?.sectionId === sectionId) {
            this.isEditorSelected = false;
            _editorSelectedSectionId = null;
            this.close();
          }
        };

        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:block:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
        document.addEventListener('shopify:block:deselect', this.onEditorDeselect);

        this.setup();

        const sectionId = getSectionId();
        if (_editorSelectedSectionId && sectionId === _editorSelectedSectionId) {
          this.isEditorSelected = true;
          this.open();
        }
      }
    }
  }

  window.Universe = window.Universe || {};
  window.Universe.CartDrawer = CartDrawer;
})();
